(ns serv-ui.live
  "The operator prompts window's one connection to the server: a WebSocket
  at /api/live (`serv.live` has the frames). `start!` puts `fetch>` in place
  of `js/fetch` for all of `serv-ui.api`, so every request of this window -
  sign-in, list, answer, files, push - goes over it, and nothing else does.

  What the server says by itself is that a prompt changed; `!changes` counts
  that, and the reconnects, after which anything may have. `use-live` loads
  a path again whenever it moves."
  (:require [clojure.string :as str]
            [kc.core :as kc]
            [serv-ui.api :as api]))

(defonce !state (atom :connecting)) ; :connecting :open :lost
(defonce !changes (atom 0))

(defonce ^:private !ws (atom nil))
(defonce ^:private !pending (atom {})) ; id -> [resolve reject]
(defonce ^:private !queue (atom []))   ; frames waiting for the socket
(defonce ^:private !next-id (atom 0))
(defonce ^:private !backoff (atom 0))

(def ^:private encoder (js/TextEncoder.))
(def ^:private decoder (js/TextDecoder.))

(defn- frame [head ^js body]
  (let [h   (.encode encoder (js/JSON.stringify head))
        b   (js/Uint8Array. body)
        out (js/Uint8Array. (+ 4 (.-length h) (.-length b)))]
    (.setUint32 (js/DataView. (.-buffer out)) 0 (.-length h))
    (.set out h 4)
    (.set out b (+ 4 (.-length h)))
    out))

(defn- unframe [^js buf]
  (let [n (.getUint32 (js/DataView. buf) 0)]
    [(js/JSON.parse (.decode decoder (js/Uint8Array. buf 4 n)))
     (.slice buf (+ 4 n))]))

(defn- url []
  (let [origin (if (seq api/base) api/base js/location.origin)]
    (str (str/replace origin #"^http" "ws") "/api/live")))

;;; ------------------------------------------------------------ the socket

(declare connect!)

(defn- send! [f]
  (let [^js ws @!ws]
    (if (and ws (= 1 (.-readyState ws)))
      (.send ws f)
      (swap! !queue conj f))))

(defn- answered! [^js buf]
  (let [[^js head body] (unframe buf)
        status (.-status head)]
    (when-let [[resolve _] (get @!pending (.-id head))]
      (swap! !pending dissoc (.-id head))
      (resolve (js/Response. (when-not (#{101 204 205 304} status) body)
                             #js {:status status :headers (.-headers head)})))))

(defn- news! [text]
  (let [^js m (js/JSON.parse text)]
    (when (= "changed" (.-type m))
      (swap! !changes inc))))

(defn- lost! []
  (let [pending @!pending]
    (reset! !pending {})
    (doseq [[_ [_ reject]] pending]
      (reject (ex-info "The connection to the server was lost." {:status 0}))))
  (reset! !state :lost)
  (let [ms (min 10000 (* 500 (js/Math.pow 2 @!backoff)))]
    (swap! !backoff inc)
    (js/setTimeout connect! ms)))

(defn- connect! []
  (let [ws (js/WebSocket. (url))]
    (set! (.-binaryType ws) "arraybuffer")
    (reset! !ws ws)
    (set! (.-onopen ws)
          (fn [_]
            (let [again? (= :lost @!state)
                  q      @!queue]
              (reset! !backoff 0)
              (reset! !queue [])
              (reset! !state :open)
              (doseq [f q] (.send ws f))
              (when again? (swap! !changes inc)))))
    (set! (.-onmessage ws)
          (fn [^js e]
            (if (string? (.-data e)) (news! (.-data e)) (answered! (.-data e)))))
    (set! (.-onclose ws) (fn [_] (when (= ws @!ws) (lost!))))))

(defn fetch>
  "`js/fetch`, over the socket: the request as it would have gone over HTTP,
  answered with a Response."
  [href opts]
  (let [req (js/Request. (js/URL. href js/location.href) opts)
        u   (js/URL. (.-url req))
        id  (swap! !next-id inc)
        ;; before the body is read: a FormData's boundary is in here
        headers #js {}]
    (.forEach (.-headers req) (fn [v k] (aset headers k v)))
    (-> (.arrayBuffer req)
        (.then (fn [body]
                 (js/Promise.
                  (fn [resolve reject]
                    (swap! !pending assoc id [resolve reject])
                    (send! (frame #js {:id id :method (.-method req)
                                       :path (str (.-pathname u) (.-search u))
                                       :headers headers}
                                  body)))))))))

(defn start! []
  (reset! api/!fetch fetch>)
  (when-not @!ws (connect!)))

;;; ----------------------------------------------------------------- hooks

(defn use-live
  "`path` (EDN) loaded, and loaded again whenever a prompt changes or the
  connection comes back. Answers `{:data :error :status :reload}`; a reload
  that fails keeps what was there and says nothing."
  [path]
  (let [[state set-state] (kc/use-state {})
        [n set-n]         (kc/use-state 0)
        changes           (kc/use-atom !changes)]
    (kc/use-effect
     [path n changes]
     (let [live? (volatile! true)]
       (-> (api/get> path)
           (.then #(when @live? (set-state {:data %})))
           (.catch #(when @live?
                      (set-state (fn [s]
                                   (if (and (:data s) (not= 404 (:status (ex-data %))))
                                     s
                                     {:error (or (ex-message %) (str %))
                                      :status (:status (ex-data %))}))))))
       (fn [] (vreset! live? false))))
    (assoc state :reload #(set-n inc))))
